import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import * as API from "../utils/api";
import {
  getCustomerSyncRunTimestamp,
  isActiveCustomerSyncStatus,
  TERMINAL_CUSTOMER_SYNC_STATUSES,
} from "../utils/helpers";

const STORAGE_KEY = "qbCustomerSyncActive";
const POLL_FAILURE_THRESHOLD = 3;

export const CustomerSyncContext = createContext(null);

export const useCustomerSync = () => {
  const context = useContext(CustomerSyncContext);
  if (!context) {
    throw new Error("useCustomerSync must be used within CustomerSyncProvider");
  }
  return context;
};

const readStoredActiveSyncs = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStoredActiveSyncs = (activeSyncs) => {
  try {
    if (!activeSyncs || Object.keys(activeSyncs).length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(activeSyncs));
  } catch {
    // Ignore storage failures.
  }
};

const normalizeSyncRun = (syncRun) => {
  if (!syncRun || typeof syncRun !== "object") return null;
  return syncRun;
};

const buildSyncState = (syncRun) => {
  const run = normalizeSyncRun(syncRun);
  if (!run) return null;

  const status = run.sync_status ?? "";
  const isActive = isActiveCustomerSyncStatus(status);
  const lastSyncedAt =
    status === "completed" ? getCustomerSyncRunTimestamp(run) : null;

  return {
    syncRunId: run.id,
    userId: run.user_id,
    status,
    phase: run.phase ?? null,
    isActive,
    lastSyncedAt,
    syncRun: run,
  };
};

const hasSameSyncProgress = (previousState, nextState) => {
  if (!previousState || !nextState) return false;

  return (
    previousState.syncRunId === nextState.syncRunId &&
    previousState.status === nextState.status &&
    previousState.phase === nextState.phase &&
    previousState.isActive === nextState.isActive &&
    previousState.lastSyncedAt === nextState.lastSyncedAt &&
    previousState.syncRun?.synced_invoices ===
      nextState.syncRun?.synced_invoices &&
    previousState.syncRun?.total_invoices === nextState.syncRun?.total_invoices
  );
};

export const CustomerSyncProvider = ({ children }) => {
  const [syncByUserId, setSyncByUserId] = useState({});
  const syncByUserIdRef = useRef({});
  const completeCallbacksRef = useRef(new Map());
  const restoredActiveSyncsRef = useRef(false);
  const pollSyncRunRef = useRef(null);
  const pollFailureCountRef = useRef(new Map());
  const inFlightRef = useRef(new Set());

  const setUser = useCallback((user) => {
    if (
      restoredActiveSyncsRef.current ||
      !user ||
      typeof user !== "object" ||
      !(user.user_auth_token || user.id)
    ) {
      return;
    }

    const stored = readStoredActiveSyncs();
    if (Object.keys(stored).length === 0) return;

    restoredActiveSyncsRef.current = true;
    Object.entries(stored).forEach(([userId, syncRunId]) => {
      pollSyncRunRef.current?.(user, syncRunId, userId);
    });
  }, []);

  const registerSyncCompleteCallback = useCallback((userId, callback) => {
    const key = String(userId);
    completeCallbacksRef.current.set(key, callback);
    return () => {
      completeCallbacksRef.current.delete(key);
    };
  }, []);

  const updateActiveSyncStorage = useCallback((nextMap) => {
    const activeSyncs = Object.entries(nextMap).reduce((acc, [userId, state]) => {
      if (state?.isActive && state.syncRunId != null) {
        acc[userId] = state.syncRunId;
      }
      return acc;
    }, {});
    writeStoredActiveSyncs(activeSyncs);
  }, []);

  const applySyncRun = useCallback(
    (syncRun) => {
      const nextState = buildSyncState(syncRun);
      if (!nextState?.userId) return null;

      const userIdKey = String(nextState.userId);
      const previousState = syncByUserIdRef.current[userIdKey] ?? null;
      const resolvedState = {
        ...nextState,
        lastSyncedAt:
          nextState.lastSyncedAt ?? previousState?.lastSyncedAt ?? null,
      };
      if (hasSameSyncProgress(previousState, resolvedState)) return resolvedState;

      const storedActiveSyncs = readStoredActiveSyncs();
      const wasActive =
        previousState?.isActive === true ||
        (previousState == null &&
          storedActiveSyncs[userIdKey] === resolvedState.syncRunId);

      const merged = {
        ...syncByUserIdRef.current,
        [userIdKey]: resolvedState,
      };
      syncByUserIdRef.current = merged;
      setSyncByUserId(merged);
      updateActiveSyncStorage(merged);

      if (wasActive) {
        if (resolvedState.status === "completed") {
          toast.success("QuickBooks sync completed");
          completeCallbacksRef.current.get(userIdKey)?.();
        } else if (resolvedState.status === "failed") {
          toast.error("QuickBooks sync failed");
        } else if (resolvedState.status === "cancelled") {
          toast.error("QuickBooks sync was cancelled");
        }
      }

      return resolvedState;
    },
    [updateActiveSyncStorage]
  );

  const abandonActiveSyncDueToPollFailure = useCallback(
    (userId, syncRunId) => {
      const userIdKey = String(userId);
      const previousState = syncByUserIdRef.current[userIdKey];
      if (!previousState?.isActive) return;

      const nextState = {
        ...previousState,
        status: "failed",
        isActive: false,
        lastSyncedAt: previousState?.lastSyncedAt ?? null,
        syncRun: {
          ...(previousState.syncRun ?? {}),
          id: syncRunId,
          user_id: userId,
          sync_status: "failed",
        },
      };

      const merged = { ...syncByUserIdRef.current, [userIdKey]: nextState };
      syncByUserIdRef.current = merged;
      setSyncByUserId(merged);
      updateActiveSyncStorage(merged);
      toast.error("Unable to check QuickBooks sync status");
    },
    [updateActiveSyncStorage]
  );

  const recordPollFailure = useCallback(
    (syncRunId, userId) => {
      const key = String(syncRunId);
      const nextCount = (pollFailureCountRef.current.get(key) ?? 0) + 1;
      pollFailureCountRef.current.set(key, nextCount);

      if (nextCount >= POLL_FAILURE_THRESHOLD) {
        pollFailureCountRef.current.delete(key);
        abandonActiveSyncDueToPollFailure(userId, syncRunId);
      }
    },
    [abandonActiveSyncDueToPollFailure]
  );

  const resetPollFailureCount = useCallback((syncRunId) => {
    pollFailureCountRef.current.delete(String(syncRunId));
  }, []);

  const fetchLatestSyncRun = useCallback(
    async (user, userId) => {
      if (!user || userId == null) return null;

      try {
        const response = await API.getLatestCustomerSyncRun(user, userId);
        if (response?.status_code === 404) return null;

        const syncRun = normalizeSyncRun(response?.data ?? response);
        if (!syncRun) return null;

        return applySyncRun(syncRun);
      } catch (error) {
        console.error("Customer sync latest fetch error:", error);
        return null;
      }
    },
    [applySyncRun]
  );

  const startFullResync = useCallback(
    async (user, userId) => {
      if (!user || userId == null) {
        return { ok: false, message: "Unable to start sync." };
      }

      const userIdKey = String(userId);
      if (
        syncByUserIdRef.current[userIdKey]?.isActive ||
        inFlightRef.current.has(userIdKey)
      ) {
        return { ok: false, message: "Sync already in progress." };
      }

      inFlightRef.current.add(userIdKey);
      try {
        const response = await API.startCustomerFullResync(user, userId);
        if (response?.status_code !== 202) {
          return {
            ok: false,
            message:
              response?.message ??
              response?.error ??
              "Unable to start QuickBooks sync.",
          };
        }

        const syncRun = normalizeSyncRun(response?.data);
        if (!syncRun?.id) {
          return {
            ok: false,
            message: "Sync started but no run id was returned.",
          };
        }

        applySyncRun(syncRun);
        toast.success("QuickBooks sync started");
        return { ok: true, syncRun };
      } catch {
        return {
          ok: false,
          message: "Unable to start QuickBooks sync. Please try again.",
        };
      } finally {
        inFlightRef.current.delete(userIdKey);
      }
    },
    [applySyncRun]
  );

  const pollSyncRun = useCallback(
    async (user, syncRunId, userId) => {
      if (!user || syncRunId == null) return null;

      try {
        const response = await API.getCustomerSyncRun(user, syncRunId);
        if (response?.status_code !== 200) {
          console.error(
            "Customer sync poll failed:",
            response?.status_code,
            response?.message ?? response?.error
          );
          recordPollFailure(syncRunId, userId);
          return null;
        }

        const syncRun = normalizeSyncRun(response?.data ?? response);
        if (!syncRun) {
          console.error(
            "Customer sync poll returned no sync run data:",
            syncRunId
          );
          recordPollFailure(syncRunId, userId);
          return null;
        }

        resetPollFailureCount(syncRunId);
        return applySyncRun(syncRun);
      } catch (error) {
        console.error("Customer sync poll error:", error);
        recordPollFailure(syncRunId, userId);
        return null;
      }
    },
    [applySyncRun, recordPollFailure, resetPollFailureCount]
  );

  useEffect(() => {
    pollSyncRunRef.current = pollSyncRun;
  }, [pollSyncRun]);

  const getSyncState = useCallback(
    (userId) => {
      if (userId == null) return null;
      return syncByUserId[String(userId)] ?? null;
    },
    [syncByUserId]
  );

  const activeSyncs = useMemo(
    () =>
      Object.values(syncByUserId).filter(
        (state) => state?.isActive && state.syncRunId != null
      ),
    [syncByUserId]
  );

  const value = useMemo(
    () => ({
      syncByUserId,
      activeSyncs,
      setUser,
      getSyncState,
      startFullResync,
      fetchLatestSyncRun,
      pollSyncRun,
      registerSyncCompleteCallback,
      terminalStatuses: TERMINAL_CUSTOMER_SYNC_STATUSES,
    }),
    [
      syncByUserId,
      activeSyncs,
      setUser,
      getSyncState,
      startFullResync,
      fetchLatestSyncRun,
      pollSyncRun,
      registerSyncCompleteCallback,
    ]
  );

  return (
    <CustomerSyncContext.Provider value={value}>
      {children}
    </CustomerSyncContext.Provider>
  );
};
